import React from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  MapPin,
  Clock,
  Navigation,
  Car,
  AlertCircle
} from 'lucide-react';
import { api } from '../../utils/api';
import DriverMap from '../../components/DriverMap';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

const DriverLocation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get navigation context from URL params
  const urlParams = new URLSearchParams(location.search);
  const from = urlParams.get('from');
  const sourceId = urlParams.get('sourceId');

  const handleBackNavigation = () => {
    // Check if we came from driver details and preserve the original source context
    if (from === 'drivers' && sourceId) {
      // Get the original source from URL parameters to determine proper navigation
      const originalSourceParam = urlParams.get('originalSource');
      if (originalSourceParam === 'pending') {
        navigate(`/drivers/${sourceId}?from=pending&sourceId=${sourceId}`);
      } else {
        navigate(`/drivers/${sourceId}`);
      }
    } else if (from === 'pending') {
      navigate('/drivers/pending');
    } else {
      navigate('/drivers');
    }
  };

  const { data, isLoading, error } = useQuery(
    ['driver-location', id],
    () => api.get(`/drivers/${id}`),
    {
      enabled: !!id,
      refetchInterval: 10000 // Refetch every 10 seconds for real-time updates
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Driver not found</h3>
        <p className="empty-state-description">
          The driver you're looking for doesn't exist.
        </p>
        <Link to="/drivers" className="btn btn-primary mt-4">
          Back to Drivers
        </Link>
      </div>
    );
  }

  const driverData = data?.data?.data || data?.data;
  const driver = driverData?.driver || driverData;

  if (!driver) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Driver not found</h3>
        <p className="empty-state-description">
          The driver you're looking for doesn't exist.
        </p>
        <button onClick={handleBackNavigation} className="btn btn-primary mt-4">
          Back to Drivers
        </button>
      </div>
    );
  }

  const formatLocation = (location) => {
    if (!location || !Array.isArray(location.coordinates) || location.coordinates.length < 2) {
      return 'N/A';
    }
    return `${location.coordinates[1].toFixed(4)}, ${location.coordinates[0].toFixed(4)}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center space-x-4">
            <button 
              onClick={handleBackNavigation}
              className="text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="page-title">Driver Location</h1>
              <p className="page-subtitle">
                Real-time location tracking for {driver.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Location */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Current Location</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Coordinates</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatLocation(driver.location)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Last Updated</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatTime(driver.location?.updatedAt)}
                </span>
              </div>
              
              {driver.location?.tripId && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Trip</span>
                  <Link 
                    to={`/trips/${driver.location.tripId}?from=location&sourceId=${id}${from ? `&originalSource=${from}` : ''}${sourceId && from !== 'details' ? `&driverSource=${sourceId}` : ''}`}
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    View Trip
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live Stats */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Live Stats</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Battery</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.liveStats?.battery || 0}%
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Speed</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.liveStats?.speed || 0} km/h
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Activity</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.liveStats?.activity || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Map */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Location Map</h3>
        </div>
        <div className="card-body p-0">
          <DriverMap driver={driver} height="400px" />
        </div>
      </div>

      {/* Home Location */}
      {driver.homeLocation && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Home Location</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Address</span>
                <span className="text-sm font-medium text-gray-900 text-right">
                  {driver.homeLocation.addressName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Coordinates</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatLocation(driver.homeLocation)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverLocation; 